// =============================================
// Admin Products — קטלוג מוצרים
// Dual-mode: Network Manager (OwnerLevel=1) + SysAdmin (OwnerLevel=0)
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Search, Pencil, Trash2, X,
  Tag, DollarSign, Ruler, ToggleLeft, ToggleRight,
  Inbox, Check, XCircle, Clock, ChevronDown, Building2,
  Loader2
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import { adminApi } from '../../services/api';
import type { Product, ExpenseCategory } from '../../stores/teacherStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminProductHelpTexts } from '../../constants/helpTexts';
import { ModalWrapper } from '../../components/shared/ModalWrapper';
import { SuccessToast } from '../../components/shared/SuccessToast';
import { useSuccessToast } from '../../hooks/useSuccessToast';
import { adminColors } from '../../styles/colors';

const colors = {
  ...adminColors,
  sysAdmin: '#7c3aed',
  sysAdminLight: '#f5f3ff',
  green: '#22c55e',
  greenLight: '#dcfce7',
  red: '#ef4444',
  redLight: '#fef2f2',
  orange: '#f59e0b',
  orangeLight: '#fffbeb',
};

interface ProductRequest {
  id: number;
  productName: string;
  categoryId?: number;
  categoryName?: string;
  defaultUnit?: string;
  estimatedPrice?: number;
  isChargeable: boolean;
  notes?: string;
  status: number;
  requestedByName: string;
  kindergartenName: string;
  createdAt: string;
}

export default function AdminProducts() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();

  // Detect mode: SysAdmin (no org) vs Network Manager (has org)
  const isSysAdmin = !user?.organizationPublicId;
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;
  const themeColor = isSysAdmin ? colors.sysAdmin : colors.primary;
  const themeLight = isSysAdmin ? colors.sysAdminLight : colors.primaryLight;

  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [productRequests, setProductRequests] = useState<ProductRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'requests'>('products');
  const { message: successMessage, show: showSuccess } = useSuccessToast();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formName, setFormName] = useState('');
  const [formCategoryId, setFormCategoryId] = useState<number | ''>('');
  const [formUnit, setFormUnit] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formChargeable, setFormChargeable] = useState(true);
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Reject modal
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load categories (pass orgFilter so admin endpoint is used correctly)
        const catResult = await adminApi.getCategories(orgFilter);
        if (catResult.success && catResult.data) {
          setCategories(catResult.data);
        }

        // Load products based on mode
        if (isSysAdmin && !orgFilter) {
          // SysAdmin without org selected — system products
          const prodResult = await adminApi.getSystemProducts();
          if (prodResult.success && prodResult.data) {
            setProducts(prodResult.data);
          }
        } else if (orgFilter) {
          // Network manager or SysAdmin with org selected
          const prodResult = await adminApi.getProducts(orgFilter);
          if (prodResult.success && prodResult.data) {
            setProducts(prodResult.data);
          }
          // Also load product requests
          const reqResult = await adminApi.getProductRequests(orgFilter);
          if (reqResult.success && reqResult.data) {
            setProductRequests(reqResult.data);
          }
        }
      } catch (err) {
        console.error('Failed to load products:', err);
        setError('שגיאה בטעינת מוצרים');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgFilter, isSysAdmin]);

  // Filter products
  const filteredProducts = products.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedCategory !== null && p.categoryId !== selectedCategory) return false;
    return true;
  });

  // Open add/edit modal
  const openAddModal = () => {
    setEditingProduct(null);
    setFormName('');
    setFormCategoryId('');
    setFormUnit('');
    setFormPrice('');
    setFormChargeable(true);
    setFormActive(true);
    setShowModal(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormName(product.name);
    setFormCategoryId(product.categoryId || '');
    setFormUnit(product.defaultUnit || '');
    setFormPrice(product.estimatedPrice?.toString() || '');
    setFormChargeable(product.isChargeable);
    setFormActive(product.isActive);
    setShowModal(true);
  };

  // Save product
  const handleSave = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: formName.trim(),
        categoryId: formCategoryId ? Number(formCategoryId) : undefined,
        defaultUnit: formUnit.trim() || undefined,
        estimatedPrice: formPrice ? parseFloat(formPrice) : undefined,
        isChargeable: formChargeable,
      };

      if (editingProduct) {
        // Update
        const result = isSysAdmin && !orgFilter
          ? await adminApi.updateSystemProduct(editingProduct.publicId, { ...data, isActive: formActive })
          : await adminApi.updateProduct(editingProduct.publicId, { ...data, isActive: formActive });
        if (result.success && result.data) {
          setProducts(prev => prev.map(p => p.publicId === editingProduct.publicId ? result.data : p));
          showSuccess('מוצר עודכן בהצלחה');
        }
      } else {
        // Create
        const result = isSysAdmin && !orgFilter
          ? await adminApi.createSystemProduct(data)
          : await adminApi.createProduct(data, orgFilter);
        if (result.success && result.data) {
          setProducts(prev => [...prev, result.data]);
          showSuccess('מוצר נוסף בהצלחה');
        }
      }
      setShowModal(false);
    } catch (err) {
      console.error('Failed to save product:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete product
  const handleDelete = async (publicId: string) => {
    try {
      const result = isSysAdmin && !orgFilter
        ? await adminApi.deleteSystemProduct(publicId)
        : await adminApi.deleteProduct(publicId);
      if (result.success) {
        setProducts(prev => prev.filter(p => p.publicId !== publicId));
        showSuccess('מוצר נמחק בהצלחה');
      }
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
    setDeletingId(null);
  };

  // Approve product request
  const handleApprove = async (requestId: number) => {
    try {
      const result = await adminApi.reviewProductRequest(requestId, 'approve');
      if (result.success) {
        setProductRequests(prev => prev.filter(r => r.id !== requestId));
        if (result.data?.publicId) {
          setProducts(prev => [...prev, result.data]);
        }
        showSuccess('בקשה אושרה — המוצר נוסף לקטלוג');
      }
    } catch (err) {
      console.error('Failed to approve request:', err);
    }
  };

  // Reject product request
  const handleReject = async (requestId: number) => {
    try {
      const result = await adminApi.reviewProductRequest(requestId, 'reject', rejectNotes || undefined);
      if (result.success) {
        setProductRequests(prev => prev.filter(r => r.id !== requestId));
        showSuccess('בקשה נדחתה');
      }
    } catch (err) {
      console.error('Failed to reject request:', err);
    }
    setRejectingId(null);
    setRejectNotes('');
  };

  const getCategoryName = (catId?: number) => {
    if (!catId) return '';
    return categories.find(c => c.id === catId)?.name || '';
  };

  const getCategoryIcon = (catId?: number) => {
    if (!catId) return '';
    return categories.find(c => c.id === catId)?.icon || '';
  };

  // Need org for network mode
  const needsOrg = !isSysAdmin || orgFilter;
  const showRequests = !!orgFilter; // Only show requests tab when we have org context

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={24} color={themeColor} />
            קטלוג מוצרים
          </h1>
          <p style={{ fontSize: '13px', color: colors.textLight, margin: '4px 0 0 0' }}>
            {isSysAdmin && !orgFilter ? 'מוצרים כלל-מערכתיים' : 'מוצרים ברמת רשת'}
            {' '} · {products.length} מוצרים
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', backgroundColor: themeColor, color: 'white',
            border: 'none', borderRadius: '10px', cursor: 'pointer',
            fontSize: '13px', fontWeight: '600',
          }}
        >
          <Plus size={16} /> הוסף מוצר
        </button>
      </div>

      {/* Success message */}
      <SuccessToast message={successMessage} />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-center">{error}</div>
      )}

      {/* Tabs (network mode only) */}
      {showRequests && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', backgroundColor: colors.background, padding: '4px', borderRadius: '10px' }}>
          <button
            onClick={() => setActiveTab('products')}
            style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '600', transition: 'all 0.2s',
              backgroundColor: activeTab === 'products' ? colors.white : 'transparent',
              color: activeTab === 'products' ? themeColor : colors.textLight,
              boxShadow: activeTab === 'products' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            מוצרים ({products.length})
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '600', transition: 'all 0.2s',
              backgroundColor: activeTab === 'requests' ? colors.white : 'transparent',
              color: activeTab === 'requests' ? colors.orange : colors.textLight,
              boxShadow: activeTab === 'requests' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            בקשות ממתינות ({productRequests.length})
          </button>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <Search size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.textLight }} />
            <input
              type="text"
              placeholder="חיפוש מוצר..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '10px 40px 10px 12px',
                border: `1px solid ${colors.border}`, borderRadius: '10px',
                fontSize: '13px', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Category filter chips */}
          {categories.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  padding: '6px 14px', border: `1px solid ${selectedCategory === null ? themeColor : colors.border}`,
                  borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                  backgroundColor: selectedCategory === null ? themeLight : 'transparent',
                  color: selectedCategory === null ? themeColor : colors.textLight,
                  whiteSpace: 'nowrap',
                }}
              >
                הכל
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                  style={{
                    padding: '6px 14px', border: `1px solid ${selectedCategory === cat.id ? themeColor : colors.border}`,
                    borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                    backgroundColor: selectedCategory === cat.id ? themeLight : 'transparent',
                    color: selectedCategory === cat.id ? themeColor : colors.textLight,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
              <Loader2 size={24} style={{ margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
              <p style={{ fontSize: '13px' }}>טוען מוצרים...</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && filteredProducts.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              backgroundColor: colors.white, borderRadius: '14px',
              border: `1px solid ${colors.border}`,
            }}>
              <Package size={40} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>
                {searchQuery || selectedCategory ? 'לא נמצאו מוצרים' : 'אין מוצרים בקטלוג'}
              </h3>
              <p style={{ fontSize: '13px', color: colors.textLight }}>
                {searchQuery || selectedCategory ? 'נסה לשנות את החיפוש או הסינון' : 'הוסף מוצרים לקטלוג כדי שגננות יוכלו להשתמש בהם'}
              </p>
            </div>
          )}

          {/* Product list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.publicId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {/* Product info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{product.name}</span>
                    {!product.isActive && (
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: colors.redLight, color: colors.red, fontWeight: '500' }}>לא פעיל</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: colors.textLight, flexWrap: 'wrap' }}>
                    {product.categoryName && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Tag size={11} /> {product.categoryName}
                      </span>
                    )}
                    {product.defaultUnit && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Ruler size={11} /> {product.defaultUnit}
                      </span>
                    )}
                    {product.estimatedPrice != null && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <DollarSign size={11} /> ₪{product.estimatedPrice.toFixed(2)}
                      </span>
                    )}
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: '3px',
                      color: product.isChargeable ? colors.orange : colors.green,
                    }}>
                      {product.isChargeable ? 'מחויב' : 'חינם'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => openEditModal(product)}
                    style={{
                      padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                      backgroundColor: 'transparent', cursor: 'pointer',
                    }}
                    title="עריכה"
                  >
                    <Pencil size={14} color={colors.textLight} />
                  </button>
                  {deletingId === product.publicId ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => handleDelete(product.publicId)}
                        style={{ padding: '8px', border: 'none', borderRadius: '8px', backgroundColor: colors.red, cursor: 'pointer' }}
                        title="אישור מחיקה"
                      >
                        <Check size={14} color="white" />
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        style={{ padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px', backgroundColor: 'transparent', cursor: 'pointer' }}
                        title="ביטול"
                      >
                        <X size={14} color={colors.textLight} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(product.publicId)}
                      style={{
                        padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                        backgroundColor: 'transparent', cursor: 'pointer',
                      }}
                      title="מחיקה"
                    >
                      <Trash2 size={14} color={colors.red} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && showRequests && (
        <>
          {productRequests.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              backgroundColor: colors.white, borderRadius: '14px',
              border: `1px solid ${colors.border}`,
            }}>
              <Inbox size={40} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>
                אין בקשות ממתינות
              </h3>
              <p style={{ fontSize: '13px', color: colors.textLight }}>
                כשגננות יבקשו להוסיף מוצרים לקטלוג, הבקשות יופיעו כאן
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {productRequests.map((req, index) => (
                <motion.div
                  key={req.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}`,
                    padding: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 4px 0' }}>{req.productName}</h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: colors.textLight }}>
                        <span>{req.requestedByName}</span>
                        <span>·</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Building2 size={11} /> {req.kindergartenName}
                        </span>
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Clock size={11} /> {new Date(req.createdAt).toLocaleDateString('he-IL')}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: colors.textLight, marginBottom: '8px', flexWrap: 'wrap' }}>
                    {req.categoryName && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Tag size={11} /> {req.categoryName}
                      </span>
                    )}
                    {req.defaultUnit && <span><Ruler size={11} /> {req.defaultUnit}</span>}
                    {req.estimatedPrice != null && <span>₪{req.estimatedPrice.toFixed(2)}</span>}
                    <span style={{ color: req.isChargeable ? colors.orange : colors.green }}>
                      {req.isChargeable ? 'מחויב' : 'חינם'}
                    </span>
                  </div>

                  {req.notes && (
                    <p style={{ fontSize: '12px', color: colors.text, backgroundColor: colors.background, padding: '8px 12px', borderRadius: '8px', margin: '0 0 10px 0' }}>
                      {req.notes}
                    </p>
                  )}

                  {/* Reject notes input */}
                  {rejectingId === req.id ? (
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="סיבת דחייה (אופציונלי)..."
                        value={rejectNotes}
                        onChange={e => setRejectNotes(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
                          borderRadius: '8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                          marginBottom: '8px',
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleReject(req.id)}
                          style={{
                            flex: 1, padding: '8px', border: 'none', borderRadius: '8px',
                            backgroundColor: colors.red, color: 'white', cursor: 'pointer',
                            fontSize: '12px', fontWeight: '600',
                          }}
                        >
                          אישור דחייה
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectNotes(''); }}
                          style={{
                            padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                            backgroundColor: 'transparent', cursor: 'pointer', fontSize: '12px',
                          }}
                        >
                          ביטול
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApprove(req.id)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '10px', border: 'none', borderRadius: '8px',
                          backgroundColor: colors.greenLight, color: '#166534', cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600',
                        }}
                      >
                        <Check size={14} /> אשר
                      </button>
                      <button
                        onClick={() => setRejectingId(req.id)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '10px', border: 'none', borderRadius: '8px',
                          backgroundColor: colors.redLight, color: colors.red, cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600',
                        }}
                      >
                        <XCircle size={14} /> דחה
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <ModalWrapper isOpen={showModal} onClose={() => setShowModal(false)} maxWidth="440px">
        <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.text, margin: 0 }}>
                  {editingProduct ? 'עריכת מוצר' : 'הוספת מוצר'}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={18} color={colors.textLight} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Name */}
                <div>
                  <FormLabel text="שם מוצר" required helpText={adminProductHelpTexts.name.tooltip} style={{ color: colors.text }} />
                  <input
                    type="text" value={formName} onChange={e => setFormName(e.target.value)}
                    placeholder="לדוגמה: חלב 1 ליטר"
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Category */}
                <div>
                  <FormLabel text="קטגוריה" helpText={adminProductHelpTexts.category.tooltip} style={{ color: colors.text }} />
                  <select
                    value={formCategoryId} onChange={e => setFormCategoryId(e.target.value ? Number(e.target.value) : '')}
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      backgroundColor: colors.white,
                    }}
                  >
                    <option value="">ללא קטגוריה</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Unit + Price row */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <FormLabel text="יחידת מידה" helpText={adminProductHelpTexts.unit.tooltip} style={{ color: colors.text }} />
                    <input
                      type="text" value={formUnit} onChange={e => setFormUnit(e.target.value)}
                      placeholder="יחידות / ק״ג / ליטר"
                      style={{
                        width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                        borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                    <HelperText text={adminProductHelpTexts.unit.helper!} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <FormLabel text="מחיר משוער (₪)" helpText={adminProductHelpTexts.price.tooltip} style={{ color: colors.text }} />
                    <input
                      type="number" value={formPrice} onChange={e => setFormPrice(e.target.value)}
                      placeholder="0.00" min="0" step="0.01"
                      style={{
                        width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                        borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      }}
                      dir="ltr"
                    />
                    <HelperText text={adminProductHelpTexts.price.helper!} />
                  </div>
                </div>

                {/* Chargeable toggle */}
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', cursor: 'pointer' }}
                  onClick={() => setFormChargeable(!formChargeable)}
                >
                  <div>
                    <FormLabel text="מוצר מחויב" helpText={adminProductHelpTexts.chargeable.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                    <p style={{ fontSize: '11px', color: colors.textLight, margin: '2px 0 0 0' }}>
                      {formChargeable ? 'מחויב — נספר בהוצאות התקציב' : 'חינם — לא נספר בהוצאות (כמו נייר טואלט)'}
                    </p>
                  </div>
                  {formChargeable ?
                    <ToggleRight size={28} color={themeColor} /> :
                    <ToggleLeft size={28} color={colors.textLight} />
                  }
                </div>

                {/* Active toggle (edit only) */}
                {editingProduct && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', cursor: 'pointer' }}
                    onClick={() => setFormActive(!formActive)}
                  >
                    <FormLabel text="פעיל" helpText={adminProductHelpTexts.active.tooltip} style={{ color: colors.text, marginBottom: 0 }} />
                    {formActive ?
                      <ToggleRight size={28} color={colors.green} /> :
                      <ToggleLeft size={28} color={colors.textLight} />
                    }
                  </div>
                )}

                {/* Save button */}
                <button
                  onClick={handleSave}
                  disabled={saving || !formName.trim()}
                  style={{
                    width: '100%', padding: '12px',
                    backgroundColor: !formName.trim() ? colors.border : themeColor,
                    color: 'white', border: 'none', borderRadius: '10px',
                    cursor: !formName.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  {saving ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <>{editingProduct ? 'עדכון' : 'הוספה'}</>
                  )}
                </button>
              </div>
        </>
      </ModalWrapper>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
